import { DeleteButton, EditButton, List, ShowButton, getDefaultSortOrder, useTable } from "@refinedev/antd";
import type { HttpError } from "@refinedev/core";
import dayjs from "dayjs";
import { useState } from "react";
import { Badge, Form, Input, Select, Space, Table, Tag, Typography } from "antd";

import { BulkDelete } from "../../components/BulkDelete";
import { CONTENT_STATUSES, QUEST_TYPES, questTypeLabel, statusMeta } from "../../lib/constants";

interface Quest {
  id: string;
  title?: string;
  type?: string;
  status?: string;
  tags?: string[];
  activeDate?: string;
  photos?: unknown[];
  createdBy?: { username?: string };
  createdAt?: string;
  roundCount?: number;
}

interface QuestFilters {
  search?: string;
  status?: string;
  type?: string;
}

export function QuestList() {
  const { tableProps, searchFormProps, sorters, tableQuery } = useTable<Quest, HttpError, QuestFilters>({
    resource: "quests",
    onSearch: (values) => [
      { field: "search", operator: "contains", value: values.search },
      { field: "status", operator: "eq", value: values.status },
      { field: "type", operator: "eq", value: values.type },
    ],
    // Сервер умеет сортировать только по дате добавления — её и даём
    // переключать. По умолчанию новые сверху.
    sorters: { initial: [{ field: "createdAt", order: "desc" }] },
  });

  // Выбор переживает переход по страницам: можно отметить записи на
  // нескольких страницах и удалить их одним действием.
  const [selected, setSelected] = useState<string[]>([]);
  // Подписи запоминаются в момент выбора: строки с других страниц в таблице
  // уже не видны, а в отчёте об ошибках их надо назвать.
  const [labels, setLabels] = useState<Record<string, string>>({});
  const questLabel = (id: string) => labels[id] ?? "Квест без названия";
  const select = (keys: React.Key[], rows: Quest[]) => {
    setSelected(keys as string[]);
    setLabels((prev) => {
      const next = { ...prev };
      for (const row of rows) if (row) next[row.id] = row.title || "Квест без названия";
      return next;
    });
  };

  return (
    <List title="Квесты">
      <Form
        {...searchFormProps}
        layout="inline"
        style={{ marginBottom: 16, rowGap: 8 }}
        onValuesChange={() => searchFormProps.form?.submit()}
      >
        <Form.Item name="search">
          <Input.Search placeholder="Поиск по названию" allowClear style={{ width: 280 }} />
        </Form.Item>

        <Form.Item name="status">
          <Select
            placeholder="Статус"
            allowClear
            style={{ width: 180 }}
            options={CONTENT_STATUSES.map((item) => ({ value: item.value, label: item.label }))}
          />
        </Form.Item>

        <Form.Item name="type">
          <Select
            placeholder="Тип"
            allowClear
            style={{ width: 180 }}
            options={QUEST_TYPES.map((item) => ({ value: item.value, label: item.label }))}
          />
        </Form.Item>
      </Form>

      <BulkDelete
        path="/api/v1/admin/quests"
        ids={selected}
        noun={{ one: "квест", few: "квеста", many: "квестов" }}
        describe={(id) => questLabel(id)}
        onClear={() => setSelected([])}
        onDone={() => {
          setSelected([]);
          void tableQuery.refetch();
        }}
      />

      <Table
        {...tableProps}
        rowSelection={{
          selectedRowKeys: selected,
          onChange: select,
          preserveSelectedRowKeys: true,
        }}
        rowKey="id"
        scroll={{ x: 900 }}
      >
        <Table.Column<Quest>
          title="Название"
          dataIndex="title"
          render={(title?: string, record?: Quest) => (
            <Space direction="vertical" size={0}>
              <Typography.Text strong>{title}</Typography.Text>
              {record?.tags && record.tags.length > 0 && (
                <Space size={4} wrap>
                  {record.tags.map((tag) => (
                    <Tag key={tag} style={{ marginInlineEnd: 0 }}>
                      {tag}
                    </Tag>
                  ))}
                </Space>
              )}
            </Space>
          )}
        />

        <Table.Column<Quest>
          title="Тип"
          dataIndex="type"
          width={160}
          render={(type?: string, record?: Quest) => (
            <Space direction="vertical" size={0}>
              <span>{questTypeLabel(type)}</span>
              {record?.activeDate && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {record.activeDate}
                </Typography.Text>
              )}
            </Space>
          )}
        />

        <Table.Column<Quest>
          title="Раундов"
          dataIndex="roundCount"
          width={100}
          render={(count?: number) => <Badge count={count ?? 0} showZero overflowCount={99999} color="#d99447" />}
        />

        <Table.Column<Quest>
          title="Автор"
          dataIndex="createdBy"
          width={150}
          render={(createdBy?: Quest["createdBy"]) => createdBy?.username ?? "редакция"}
        />

        <Table.Column<Quest>
          title="Добавлено"
          dataIndex="createdAt"
          width={160}
          sorter
          sortDirections={["descend", "ascend", "descend"]}
          defaultSortOrder={getDefaultSortOrder("createdAt", sorters)}
          render={(value?: string) => (value ? dayjs(value).format("D MMM YYYY, HH:mm") : "—")}
        />

        <Table.Column<Quest>
          title="Статус"
          dataIndex="status"
          width={150}
          render={(status?: string) => {
            const meta = statusMeta(status);
            return meta ? <Tag color={meta.color}>{meta.label}</Tag> : "—";
          }}
        />

        <Table.Column<Quest>
          title="Действия"
          width={140}
          fixed="right"
          render={(_, record) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} />
              <EditButton hideText size="small" recordItemId={record.id} />
              <DeleteButton
                hideText
                size="small"
                recordItemId={record.id}
                confirmTitle="Удалить квест?"
                confirmOkText="Удалить"
                confirmCancelText="Отмена"
              />
            </Space>
          )}
        />
      </Table>
    </List>
  );
}
